import { areFacilityNamesEquivalent } from "./formatUtils";

/**
 * Validates and normalizes data from multiple sources
 *
 * @param {Object} allInOneData - Main data object
 * @param {Object} facilitiesData - Facilities data object
 * @param {Object} hudDebtData - HUD debt data object
 * @return {Object} Object containing inconsistencies and dealMap
 */
export const validateAndNormalizeData = (
  allInOneData,
  facilitiesData,
  hudDebtData
) => {
  const inconsistencies = [];
  const dealMap = new Map();

  // First pass: Collect all unique deals and facilities
  if (allInOneData) {
    Object.keys(allInOneData).forEach((dealName) => {
      if (!dealMap.has(dealName)) {
        dealMap.set(dealName, new Set());
      }
      const facilities = allInOneData[dealName]?.Facilities || {};
      Object.keys(facilities).forEach((facilityName) => {
        dealMap.get(dealName).add(facilityName);
      });
    });
  }

  // Check facilities data against dealMap
  if (facilitiesData?.Deal) {
    facilitiesData.Deal.forEach((deal) => {
      const dealName = deal.Deal;
      if (!dealMap.has(dealName)) {
        inconsistencies.push({
          type: "deal_mismatch",
          message: `Deal "${dealName}" found in facilities data but not in main data`,
          source: "facilities",
        });
      } else {
        deal.Facilities.forEach((facility) => {
          const facilityName = facility.Facilities;
          let found = false;
          dealMap.get(dealName).forEach((existingFacility) => {
            if (areFacilityNamesEquivalent(facilityName, existingFacility)) {
              found = true;
            }
          });
          if (!found) {
            inconsistencies.push({
              type: "facility_mismatch",
              message: `Facility "${facilityName}" in deal "${dealName}" found in facilities data but not in main data`,
              source: "facilities",
            });
          }
        });
      }
    });
  }

  // Check HUD debt data against dealMap
  if (hudDebtData) {
    Object.keys(hudDebtData).forEach((hudDealName) => {
      let matchFound = false;
      dealMap.forEach((facilities, dealName) => {
        if (areFacilityNamesEquivalent(hudDealName, dealName)) {
          matchFound = true;
          const facilities = hudDebtData[hudDealName]?.Facilities || {};
          Object.keys(facilities).forEach((facilityName) => {
            let facilityFound = false;
            dealMap.get(dealName).forEach((existingFacility) => {
              if (areFacilityNamesEquivalent(facilityName, existingFacility)) {
                facilityFound = true;
              }
            });
            if (!facilityFound) {
              inconsistencies.push({
                type: "facility_mismatch",
                message: `Facility "${facilityName}" in deal "${hudDealName}" found in HUD debt data but not in main data`,
                source: "hud_debt",
              });
            }
          });
        }
      });

      if (!matchFound) {
        inconsistencies.push({
          type: "deal_mismatch",
          message: `Deal "${hudDealName}" found in HUD debt data but not in main data`,
          source: "hud_debt",
        });
      }
    });
  }

  return { inconsistencies, dealMap };
};
